/**
 * MetricCard Component
 * Reusable card component for displaying key metrics with trends
 */

import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Skeleton
} from '@mui/material';
import TrendIndicator from './TrendIndicator';

export interface MetricCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color?: 'primary' | 'success' | 'warning' | 'info' | 'error';
  trend?: number; // Percentage change (positive or negative)
  trendLabel?: string; // e.g., "vs last month"
  loading?: boolean;
  subtitle?: string;
  onClick?: () => void;
}

const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  icon,
  color = 'primary',
  trend,
  trendLabel,
  loading = false,
  subtitle,
  onClick
}) => {
  // Color mapping
  const colorMap = {
    primary: '#1976d2',
    success: '#2e7d32',
    warning: '#ed6c02',
    info: '#0288d1',
    error: '#d32f2f'
  };

  const bgColorMap = {
    primary: 'rgba(25, 118, 210, 0.08)',
    success: 'rgba(46, 125, 50, 0.08)',
    warning: 'rgba(237, 108, 2, 0.08)',
    info: 'rgba(2, 136, 209, 0.08)',
    error: 'rgba(211, 47, 47, 0.08)'
  };

  return (
    <Card
      sx={{
        height: '100%',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.3s ease',
        '&:hover': onClick ? {
          boxShadow: 4,
          transform: 'translateY(-4px)'
        } : {}
      }}
      onClick={onClick}
      elevation={2}
    >
      <CardContent>
        {loading ? (
          <>
            <Skeleton variant="rectangular" width={48} height={48} sx={{ mb: 2, borderRadius: 1 }} />
            <Skeleton width="60%" height={24} sx={{ mb: 1 }} />
            <Skeleton width="80%" height={40} />
          </>
        ) : (
          <>
            {/* Icon */}
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: bgColorMap[color],
                color: colorMap[color],
                mb: 2
              }}
            >
              {icon}
            </Box>

            {/* Title */}
            <Typography
              variant="body2"
              color="text.secondary"
              gutterBottom
              sx={{ fontSize: '0.875rem', fontWeight: 500 }}
            >
              {title}
            </Typography>

            {/* Value */}
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                mb: 1,
                color: 'text.primary'
              }}
            >
              {value}
            </Typography>

            {/* Subtitle or Trend */}
            {subtitle && !trend && (
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                {subtitle}
              </Typography>
            )}

            {/* Trend Indicator */}
            {trend !== undefined && (
              <Box display="flex" alignItems="center" gap={0.5}>
                <TrendIndicator value={trend} />
                {trendLabel && (
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                    {trendLabel}
                  </Typography>
                )}
              </Box>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default MetricCard;
